import { type FC, type ReactNode } from "react";

import { type WakeBlockBadgeCopy } from "../blackboardModels";

// ---------------------------------------------------------------------------
// WakeBlocked details — surfaced when an actor has a parked wake input
// ---------------------------------------------------------------------------

export const WakeBlockedDetailsBlock: FC<{
  details: WakeBlockBadgeCopy;
  fullError: string | null;
  triggerChannelMessageId: string | null;
}> = ({ details, fullError, triggerChannelMessageId }) => {
  const error = fullError?.trim();
  // Retry wording is the policy's call, not the block's: reasons like missing
  // credits or provider configuration never get an automatic retry, so neither
  // the header copy nor the Retry field may imply one.
  const scheduledRetry = details.presentation.showsScheduledRetry
    ? details.retryTime
    : null;
  return (
    <div className="m-4 border border-amber-400/40 bg-amber-400/[0.04] p-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700/90 dark:text-amber-300/80">
          [ WAKE BLOCKED ]
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-amber-700/80 dark:text-amber-300/70">
          {scheduledRetry === null
            ? "unread wake input is parked"
            : "unread wake input is parked until retry"}
        </p>
      </div>
      {/* Same shared policy as the badge and the wizard callout — detail adds
          the explanation, it does not re-decide the copy. */}
      <p className="mb-3 text-xs text-amber-900/90 dark:text-amber-100/80">
        {details.presentation.explanation}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <WakeBlockedDetailsField label="Reason">
          {details.reasonLabel}
          {details.presentation.isUnknownReason &&
            details.presentation.reasonCode && (
              <span className="ml-1 text-[10px] text-slate-500 dark:text-slate-400">
                ({details.presentation.reasonCode})
              </span>
            )}
        </WakeBlockedDetailsField>
        {scheduledRetry !== null && (
          <WakeBlockedDetailsField label="Retry">
            {scheduledRetry}
          </WakeBlockedDetailsField>
        )}
        <WakeBlockedDetailsField label="Failures">
          {details.failureCount ?? "—"}
        </WakeBlockedDetailsField>
        {triggerChannelMessageId && (
          <WakeBlockedDetailsField label="Trigger message">
            <code className="break-all font-mono text-[10px] text-slate-600 dark:text-slate-300">
              {triggerChannelMessageId}
            </code>
          </WakeBlockedDetailsField>
        )}
      </div>
      <div className="mt-3">
        <p className="mb-1 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-amber-700/80 dark:text-amber-300/70">
          Provider error
        </p>
        <pre className="max-h-52 overflow-y-auto whitespace-pre-wrap break-words border border-amber-400/20 bg-white/50 p-2 font-mono text-[11px] leading-relaxed text-slate-800 dark:bg-slate-950/50 dark:text-slate-100">
          {error || "No provider error stored."}
        </pre>
      </div>
    </div>
  );
};

const WakeBlockedDetailsField: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-amber-700/70 dark:text-amber-300/60">
      {label}
    </p>
    <div className="mt-0.5 font-mono text-[11px] text-slate-700 dark:text-slate-200">
      {children}
    </div>
  </div>
);
